/**
 * プランタイプ
 */
export enum PlanType {
  TRIAL = "trial",
  FREE = "free",
  STANDARD = "standard",
  PREMIUM = "premium",
  BUILDER = "builder",
  EDUCATION = "education",
}

/**
 * Entitlement ステータス
 */
export enum EntitlementStatus {
  ACTIVE = "active",
  EXPIRED = "expired",
  REVOKED = "revoked",
}

/**
 * Entitlement ソースタイプ
 */
export enum EntitlementSourceType {
  PURCHASE = "purchase",
  SUBSCRIPTION = "subscription",
  MANUAL = "manual",
  PROMOTION = "promotion",
  TRIAL = "trial",
}

/**
 * ローディングステータス
 */
export enum LoadingStatus {
  IDLE = "idle",
  LOADING = "loading",
  SUCCEEDED = "succeeded",
  FAILED = "failed",
}

/**
 * 個別機能のQuotaステータス（APIレスポンス形式）
 */
export type QuotaStatus = {
  limit: number;
  used: number;
  remaining: number;
  unlimited: boolean;
  accessDenied: boolean;
  resetsAt?: string;
};

/**
 * プランごとの制限値（APIレスポンス形式）
 */
export type PlanLimits = {
  players: QuotaStatus;
  projects: QuotaStatus;
  aiChatDaily: QuotaStatus;
  aiImageDaily: QuotaStatus;
  aiImagePerRequest: QuotaStatus;
  assets: QuotaStatus;
};

/**
 * プランごとの制限値（フォールバック用の簡易形式）
 */
export type PlanLimitsSimple = {
  players: number;
  projects: number;
  aiGenerations: number | null;
};

/**
 * Entitlement の詳細情報
 */
export type EntitlementDetail = {
  id: number;
  status: EntitlementStatus;
  source_type: EntitlementSourceType;
  payment_provider: string | null;
  started_at: string;
  expires_at: string | null;
  refundable?: boolean;
  refund_deadline?: string | null;
  is_cancelled?: boolean;
  billing_period?: "monthly" | "annual" | null;
};

/**
 * アカウント単位の制限値（アカウントレベルのQuotaのみ）
 */
export type AccountLimits = {
  players: QuotaStatus;
  assets: QuotaStatus;
  chatHistoryLimit: QuotaStatus;
};

/**
 * AccountPlan API レスポンス型
 */
export type AccountPlanResponse = {
  plan: PlanType;
  entitlement: EntitlementDetail | null;
  players: QuotaStatus;
  assets: QuotaStatus;
  chatHistoryLimit?: QuotaStatus;
  playerLimits: Record<string, PlanLimits>;
};

/**
 * 購入可能なプラン情報
 */
export type SubscriptionPlanInfo = {
  id: string;
  name: string;
  product_id: string;
  price: number;
  currency: string;
  billing_period: string;
};

/**
 * サブスクリプションプラン（API レスポンス）
 */
export type SubscriptionPlan = {
  product_id: string;
  name: string;
  price: number;
  currency: string;
  billing_period: "monthly" | "annual";
};

/**
 * ネイティブパッケージ取得ステータス
 */
export enum NativePackagesStatus {
  IDLE = "idle",
  LOADING = "loading",
  SUCCEEDED = "succeeded",
  FAILED = "failed",
  NOT_SUPPORTED = "not_supported",
}

/**
 * RevenueCatから取得したパッケージ情報
 */
export type NativePackage = {
  identifier: string;
  priceString: string;
  price: number;
  currencyCode: string;
  packageType: string;
};

/**
 * ネイティブパッケージの状態
 */
export type NativePackagesState = {
  packages: NativePackage[];
  status: NativePackagesStatus;
};

/**
 * Subscription Redux State の型
 */
export type SubscriptionState = {
  plan: PlanType;
  accountLimits: AccountLimits;
  playerLimits: Record<string, PlanLimits>;
  entitlement: EntitlementDetail | null;
  status: LoadingStatus;
  nativePackages: NativePackagesState;
};
